/**
 * CSV building, sanitization, and download utilities.
 * Used only by admin export flows — never imported by production screens.
 *
 * Formula-injection defence: values starting with =, +, -, @, |, or % are
 * prefixed with a single quote so spreadsheet apps treat them as text.
 */

const FORMULA_STARTERS = new Set(['=', '+', '-', '@', '|', '%', '\t']);

function sanitizeCell(value) {
  if (value == null) return '';
  // Collapse newlines to a space so the cell stays on one row
  const str = String(value).replace(/\r?\n|\r/g, ' ').trim();
  if (str.length > 0 && FORMULA_STARTERS.has(str[0])) {
    return "'" + str;
  }
  return str;
}

function escapeCell(value) {
  const s = sanitizeCell(value);
  // Wrap in quotes when the value contains a comma or double-quote
  // (sanitizeCell already strips newlines, so no need to check for those)
  if (s.includes(',') || s.includes('"')) {
    return '"' + s.replace(/"/g, '""') + '"';
  }
  return s;
}

export function buildCSV(headers, rows) {
  const lines = [
    headers.map(escapeCell).join(','),
    ...rows.map((row) => row.map(escapeCell).join(',')),
  ];
  return lines.join('\r\n');
}

/**
 * Trigger a browser download of a CSV string.
 * Prepends a UTF-8 BOM so Excel opens the file correctly without import dialogs.
 */
export function downloadCSV(filename, content) {
  const bom = '﻿';
  const blob = new Blob([bom + content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Format a Firestore Timestamp or Date as "YYYY-MM-DD HH:MM:SS" (local time). */
export function csvDateTime(ts) {
  if (!ts) return '';
  const d = ts instanceof Date ? ts : ts.toDate?.() ?? new Date(ts);
  if (isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

/** Format a Firestore Timestamp or Date as "YYYY-MM-DD" (local time). */
export function csvDate(ts) {
  if (!ts) return '';
  const d = ts instanceof Date ? ts : ts.toDate?.() ?? new Date(ts);
  if (isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Build a clean download filename from a slug and date range. */
export function makeExportFilename(slug, dateRange) {
  const p = (n) => String(n).padStart(2, '0');
  const fmt = (d) => `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  return `florra-${slug}-${fmt(dateRange.start)}-to-${fmt(dateRange.end)}.csv`;
}
